import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { startAnalytics, trackAnalytics, trackLegacyView } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Search,
  Gift,
  Loader2,
  CalendarCheck,
  Clock,
  LayoutGrid,
  FileText,
  Play,
  Sparkles,
  Eye,
  Rows3,
  Copy,
  Filter,
  FileVideo,
  Languages,
  Mic2,
  Music,
  Presentation,
  MessageCircle,
  LogOut,
} from 'lucide-react';
import {
  showUserError,
  showUserInfo,
  showUserSuccess,
  showUserToast,
} from '@/utils/user-toast';
import { Image } from '@/components/ui/image';
import { getActiveTopics, verifyPasscode, redeemResource, getMemberByPasscode, trackTopicView } from '@/api/member';
import { getPublicContactConfig } from '@/api/admin';
import { MemberBenefitsDialog } from '@/components/member/MemberBenefitsDialog';
import { MemberHistoryDialog } from '@/components/member/MemberHistoryDialog';
import { TopicFilterDrawer } from '@/components/topic/TopicFilterDrawer';
import { useMember } from '@/contexts/MemberContext';
import {
  CONTENT_CATEGORY_LABELS,
  CONTENT_DIRECTION_LABELS,
} from '@shared/api.interface';
import type {
  ResourceTopic,
  ContentCategory,
  ContactConfig,
} from '@shared/api.interface';
import { getCycleDateRange } from '@/utils/cycle-dates';
import {
  DURATION_ALL_VALUE,
  DURATION_FILTER_OPTIONS,
  getDurationRangeLabel,
  isDurationInRanges,
  type DurationRange,
} from '@/utils/topic-duration';
import { PUBLIC_CATALOG_MODE } from '@/config/public-mode';
import { ADMIN_LOGIN_PATH } from '@/config/admin-routes';
import { useIsCoarsePointer } from '@/hooks/use-coarse-pointer';
import {
  extractMemberErrorMessage,
  getMemberErrorMessage,
  getQuotaExhaustedMessage,
  MEMBER_ERROR_MESSAGES,
} from '@/utils/member-error-message';
import { showQuotaExhaustedToast } from '@/utils/quota-exhausted-toast';
import { isValidPasscode, normalizePasscode } from '@shared/input-validation';

type MobileViewMode = 'card' | 'compact';

const RESOURCE_ITEMS = [
  { icon: FileVideo, title: '演讲背景视频', description: '原声示范、消音字幕、消音无字幕，练习和展示都能用' },
  { icon: Presentation, title: '演讲背景PPT', description: '配套演讲背景页，内含背景音乐，PPT可编辑' },
  { icon: Languages, title: '中英双语文稿', description: '中英对照演讲稿，支持打印练习' },
  { icon: Mic2, title: '示范音频', description: '标准发音示范，方便反复跟读' },
  { icon: Music, title: '背景音乐', description: '配套BGM，适配演讲节奏与氛围' },
];

const HIDE_PPT_PREVIEW = false;
const VIDEO_WATERMARK_ITEMS = Array.from({ length: 9 }, (_, index) => `${index}`);
const PROTECTED_PREVIEW_STYLE = {
  WebkitUserSelect: 'none',
  userSelect: 'none',
  WebkitTouchCallout: 'none',
  touchAction: 'manipulation',
} as any;
const DEFAULT_CONTACT_CONFIG: ContactConfig = {
  enabled: false,
  // 登录旁客服入口默认跟随“启用前台客服入口”展示，可独立关闭
  show_header_entry: true,
  method: 'qr',
  title: '购买 / 续费 / 单独获取',
  description: '支持会员开通、续费、单独获取主题、找回兑换码',
  detail_description: '支持兑换码领取、单独获取、续费咨询',
  passcode_hint: '兑换码通常为订单号，需获取可联系客服',
  button_text: '联系客服',
  wechat_id: '',
  qr_image_url: '',
  link_url: '',
};

const resolvePublicAssetUrl = (filename: string): string => {
  if (typeof document === 'undefined') {
    return filename;
  }
  const faviconHref = document
    .querySelector('link[rel="icon"]')
    ?.getAttribute('href')
    ?.trim();
  if (!faviconHref) {
    return filename;
  }

  try {
    const faviconUrl = new URL(faviconHref, window.location.href);
    faviconUrl.pathname = faviconUrl.pathname.replace(
      /favicon\.svg$/i,
      filename,
    );
    faviconUrl.search = '';
    faviconUrl.hash = '';
    return faviconUrl.toString();
  } catch {
    return faviconHref.replace(/favicon\.svg(?:\?.*)?$/i, filename);
  }
};

const PublicTopicListPage = () => {
  const navigate = useNavigate();
  const { session, login, logout, updateQuota, refreshSession } = useMember();
  const [topics, setTopics] = useState<ResourceTopic[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<ContentCategory | ''>('');
  const [durationRanges, setDurationRanges] = useState<DurationRange[]>([]);
  const [mobileViewMode, setMobileViewMode] = useState<MobileViewMode>('card');
  // 设备交互模式（触屏/桌面），与 CSS 宽度断点分离，避免手机横屏旋转后被误判为桌面
  const isCoarsePointer = useIsCoarsePointer();
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  // 详情弹窗状态
  const [detailTopic, setDetailTopic] = useState<ResourceTopic | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // 兑换码验证弹窗状态
  const [isPasscodeDialogOpen, setIsPasscodeDialogOpen] = useState(false);
  const [passcodeDialogMode, setPasscodeDialogMode] = useState<'login' | 'redeem'>('redeem');
  const [passcode, setPasscode] = useState('');
  const [passcodeError, setPasscodeError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [pendingRedeem, setPendingRedeem] = useState<{
    topic: ResourceTopic;
  } | null>(null);

  // 领取确认弹窗状态
  const [confirmTopic, setConfirmTopic] = useState<ResourceTopic | null>(null);

  const [isRedeeming, setIsRedeeming] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewVideo, setPreviewVideo] = useState<string | null>(null);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [contactConfig, setContactConfig] = useState<ContactConfig>(DEFAULT_CONTACT_CONFIG);
  const [adminTapCount, setAdminTapCount] = useState(0);
  const brandLogoSrc = resolvePublicAssetUrl('brand-logo.png');

  // 获取主题列表
  const fetchTopics = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getActiveTopics();
      setTopics(result);
    } catch (error) {
      showUserError('获取主题列表失败');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  useEffect(() => {
    if (PUBLIC_CATALOG_MODE) return;

    const fetchContactConfig = async () => {
      try {
        const result = await getPublicContactConfig();
        setContactConfig(result);
      } catch {
        setContactConfig(DEFAULT_CONTACT_CONFIG);
      }
    };
    fetchContactConfig();
  }, []);

  useEffect(() => { startAnalytics(); }, []);
  const handleContactAction = (position: 'header' | 'detail' | 'passcode') => {
    if (!contactConfig.enabled) return;
    trackAnalytics({kind:'contact',position});

    if (contactConfig.method === 'link') {
      if (!contactConfig.link_url) {
        showUserError('客服链接未配置');
        return;
      }
      window.open(contactConfig.link_url, '_blank', 'noopener,noreferrer');
      return;
    }

    const hasWechatId = !!contactConfig.wechat_id?.trim();
    const hasQrImage = !!contactConfig.qr_image_url?.trim();

    if (!hasWechatId && !hasQrImage) {
      showUserError('客服信息未配置');
      return;
    }
    setIsContactDialogOpen(true);
  };

  const handleCopyWechatId = async () => {
    const wechatId = contactConfig.wechat_id?.trim();
    if (!wechatId) {
      showUserError('微信号未配置');
      return;
    }

    try {
      await navigator.clipboard.writeText(wechatId);
      showUserSuccess('微信号已复制');
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = wechatId;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      showUserSuccess('微信号已复制');
    }
  };

  const handleHiddenAdminEntry = () => {
    const nextCount = adminTapCount + 1;
    setAdminTapCount(nextCount);

    if (nextCount >= 5) {
      setAdminTapCount(0);
      navigate(ADMIN_LOGIN_PATH);
      return;
    }

    window.setTimeout(() => {
      setAdminTapCount((current) => (current === nextCount ? 0 : current));
    }, 2500);
  };

  const isTotalMode = session?.member.quota_mode === "total";
const cycleRemainLabel = isTotalMode ? "总次数剩余" : "本周期剩余";
  const cycleDates = !isTotalMode && session?.member.cycle_start_date
    ? getCycleDateRange(session.member.cycle_start_date)
    : null;
  // 查看详情
  const handleViewDetail = (topic: ResourceTopic) => {
    trackLegacyView(() => trackTopicView(topic.topic_name));
    trackAnalytics({kind:'detail',topicId:topic.id});
    setDetailTopic(topic);
    setIsDetailOpen(true);
  };

  // 打开领取流程
  const handleOpenRedeem = (topic: ResourceTopic) => {
    if (session) {
      // 已登录，检查额度（领取动作的额度拦截保持不变）
      if (session.remainingCycle <= 0) {
        showQuotaExhaustedToast(getQuotaExhaustedMessage(session.member));
        return;
      }
      if (session.remainingToday <= 0) {
        showUserInfo(MEMBER_ERROR_MESSAGES.DAILY_LIMIT_REACHED);
        return;
      }
      setConfirmTopic(topic);
    } else {
      // 未登录，先要求输入兑换码
      setPendingRedeem({ topic });
      setPasscodeDialogMode('redeem');
      setPasscode('');
      setPasscodeError('');
      setIsPasscodeDialogOpen(true);
    }
  };

  const handleOpenLogin = () => {
    setPendingRedeem(null);
    setPasscodeDialogMode('login');
    setPasscode('');
    setPasscodeError('');
    setIsPasscodeDialogOpen(true);
  };

  const handleDurationFilterClick = (
    value: DurationRange | typeof DURATION_ALL_VALUE,
  ) => {
    if (value === DURATION_ALL_VALUE) {
      setDurationRanges([]);
      return;
    }

    setDurationRanges((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value],
    );
  };

  const handleClearFilters = () => {
    setDurationRanges([]);
    setCategory('');
  };

  // 验证兑换码
  const handleVerifyPasscode = async () => {
    const normalizedPasscode = normalizePasscode(passcode);

    if (!normalizedPasscode) {
      setPasscodeError('请输入兑换码');
      return;
    }
    if (!isValidPasscode(normalizedPasscode)) {
      setPasscodeError('兑换码必须为 6–32 位数字或英文字母；最外侧空白会自动去除，内部空格、中文和特殊符号不支持');
      return;
    }

    setIsVerifying(true);
    try {
      const result = await verifyPasscode({
        passcode: normalizedPasscode,
        topic_name: pendingRedeem?.topic.topic_name,
      });

      if (!result.success) {
        const message = getMemberErrorMessage(result.error_code);
        setPasscodeError(message);
        return;
      }

      if (result.member) {
        // 保存会话（额度用完也正常登录，不拦截登录流程）
        login({
          passcode: normalizedPasscode,
          member: result.member,
          remainingCycle: result.remaining_cycle,
          remainingToday: result.remaining_today,
        });

        // 登录成功即关闭兑换码弹窗，额度提示用轻提示展示，不再显示“登录失败”式红色错误
        setIsPasscodeDialogOpen(false);
        setPasscodeError('');

        // 周期/总额度用完：正常登录，按额度类型提示恢复时间
        if (result.remaining_cycle <= 0) {
          showQuotaExhaustedToast(getQuotaExhaustedMessage(result.member));
          return;
        }
        // 仅今日额度用完：正常登录，提示明日恢复
        if (result.remaining_today <= 0) {
          showUserInfo(MEMBER_ERROR_MESSAGES.DAILY_LIMIT_REACHED);
          return;
        }

        // 额度正常，继续领取流程
        if (pendingRedeem) {
          setConfirmTopic(pendingRedeem.topic);
          setPendingRedeem(null);
        }
      }
    } catch (error) {
      const message = extractMemberErrorMessage(error);
      setPasscodeError(message);
    } finally {
      setIsVerifying(false);
    }
  };

  // 确认领取
  const confirmRedeem = async () => {
    if (!session || !confirmTopic) return;

    setIsRedeeming(true);
    try {
      const result = await redeemResource({
        passcode: session.passcode,
        topic_name: confirmTopic.topic_name,

      });

      if (result.already_redeemed) {
        setConfirmTopic(null);
        showUserToast(getMemberErrorMessage('ALREADY_REDEEMED'));
        return;
      }

      // 更新额度
      updateQuota(result.remaining_cycle, result.remaining_today);
      try {
        const latestMember = await getMemberByPasscode(session.passcode);
        if (latestMember) {
          refreshSession({
            member: latestMember,
            remainingCycle: result.remaining_cycle,
            remainingToday: result.remaining_today,
          });
        }
      } catch {
        // 领取成功时，不让会员信息刷新失败打断主流程
      }

      // 关闭弹窗
      setConfirmTopic(null);

      // 跳转到成功页
      navigate('/success', {
        state: {
          topicName: result.topic_name,
          memberType: result.member_type,
          downloadLink: result.download_link,
          remainingCycle: result.remaining_cycle,
          remainingToday: result.remaining_today,
        },
      });
    } catch (error: any) {
      showUserError(extractMemberErrorMessage(error));
    } finally {
      setIsRedeeming(false);
    }
  };



  // 筛选主题
  const normalizedSearch = search.trim().toLowerCase();

  const getSearchRank = (topic: ResourceTopic) => {
    if (!normalizedSearch) return 0;

    const topicName = (topic.topic_name || '').toLowerCase();
    if (topicName.includes(normalizedSearch)) return 2;
    return 0;
  };

  const filteredTopics = topics
    .filter((topic) => {
      const searchRank = getSearchRank(topic);
      const matchSearch = !normalizedSearch || searchRank > 0;
      const matchCategory = !category || topic.content_category === category;
      const matchDuration = isDurationInRanges(topic.estimated_duration, durationRanges);
      return matchSearch && matchCategory && matchDuration;
    })
    .sort((a, b) => getSearchRank(b) - getSearchRank(a));

  const selectedDurationFilters = durationRanges.map((range) => ({
    value: range,
    label: getDurationRangeLabel(range),
  }));
  const selectedCategoryLabel = category ? CONTENT_CATEGORY_LABELS[category] : '';
  const hasActiveFilters = durationRanges.length > 0 || !!category;
  const selectedFilterSummary = hasActiveFilters
    ? [...selectedDurationFilters.map((filter) => filter.label), selectedCategoryLabel]
        .filter(Boolean)
        .join(' / ')
    : '全部主题';

  const getPreviewGridClassName = (count: number) => {
    if (count <= 1) return 'grid grid-cols-1 gap-3';
    return 'grid grid-cols-2 gap-3';
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      {/* 顶部栏 */}
      <div className="sticky top-0 z-10 border-b bg-white">
        <div className="mx-auto max-w-6xl px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-2">
            {/* 品牌区 */}
            <div
              className="flex shrink-0 cursor-pointer items-center gap-2 select-none"
              onClick={handleHiddenAdminEntry}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleHiddenAdminEntry();
                }
              }}
            >
              <img
                src={brandLogoSrc}
                alt="少儿英语说图标"
                className="h-8 w-8 rounded-md object-cover sm:h-9 sm:w-9"
              />
              <h1 className="text-lg font-semibold">少儿英语说</h1>
            </div>

            {/* 会员状态摘要区：独立容器；仅 md+ 显示；内容长时自行换行，不挤压操作入口 */}
            {!PUBLIC_CATALOG_MODE && session && (
              <div className="hidden min-w-0 flex-1 text-sm md:flex md:justify-end">
                <div className="flex flex-wrap items-center justify-end gap-2 text-muted-foreground">
                  <span className="font-medium text-foreground">{session.member.member_type}</span>
                  <span>|</span>
                  <span>
                    {!isTotalMode && cycleDates
                      ? `本周期（${cycleDates.startLabel} - ${cycleDates.endLabel}）剩余: `
                      : `${cycleRemainLabel}: `}
                    <strong>{session.remainingCycle}</strong>
                  </span>
                  {!isTotalMode && (
                    <>
                      <span>|</span>
                      <span>今日剩余: <strong>{session.remainingToday}</strong></span>
                    </>
                  )}
                  {!isTotalMode && cycleDates && (
                    <>
                      <span>|</span>
                      <span>下次重置: <strong>{cycleDates.nextResetDate}</strong></span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* 操作入口区：独立、稳定、不换行的紧凑布局，三个入口始终同排 */}
            {!PUBLIC_CATALOG_MODE && session && (
              <div className="flex shrink-0 items-center gap-1 sm:gap-2">
                <MemberBenefitsDialog
                  session={session}
                  triggerClassName="px-2 has-[>svg]:px-2 sm:px-3 sm:has-[>svg]:px-2.5 border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100"
                />
                <MemberHistoryDialog
                  passcode={session.passcode}
                  triggerClassName="px-2 has-[>svg]:px-2 sm:px-3 sm:has-[>svg]:px-2.5 border-orange-200 bg-white text-orange-800 hover:bg-orange-50"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className="shrink-0 px-2 has-[>svg]:px-2 text-muted-foreground sm:px-3 sm:has-[>svg]:px-2.5"
                >
                  <LogOut className="mr-1 h-4 w-4" />
                  退出
                </Button>
              </div>
            )}

            {!PUBLIC_CATALOG_MODE && !session && (
              <div className="flex shrink-0 items-center gap-1 sm:gap-2">
                {contactConfig.enabled && contactConfig.show_header_entry && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleContactAction('header')}
                    className="h-8 gap-1 px-2 text-sm text-muted-foreground hover:text-foreground"
                  >
                    <MessageCircle className="h-4 w-4" />
                    {contactConfig.button_text || '联系客服'}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleOpenLogin}
                  className="h-8 px-2 text-sm text-muted-foreground hover:text-foreground"
                >
                  登录
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 主内容区 */}
      <div className="mx-auto max-w-6xl px-4 py-6">
        {/* 搜索区 */}
        <div className="mb-6 rounded-2xl border border-orange-100 bg-white p-3 shadow-sm sm:p-4">
          <form
            className="flex flex-col gap-2 sm:flex-row"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="搜索主题名称，例如：花木兰、守株待兔、孔融让梨"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-11 rounded-xl border-orange-100 pl-10 text-base shadow-none focus-visible:ring-orange-200 sm:h-12 sm:text-sm"
              />
            </div>
            <Button
              type="submit"
              className="h-11 rounded-xl bg-orange-500 px-8 font-semibold text-white hover:bg-orange-600 sm:h-12"
            >
              搜索
            </Button>
          </form>

          <div className="mt-4 hidden space-y-3 md:block">
            <div className="flex gap-3">
              <div className="w-10 shrink-0 pt-1.5 text-sm font-semibold text-foreground">主题:</div>
              <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setCategory('')}
                  className={`h-8 rounded-full border px-4 text-sm transition-colors ${
                    category === ''
                      ? 'border-orange-500 bg-orange-500 text-white shadow-sm'
                      : 'border-orange-100 bg-white text-foreground hover:border-orange-300 hover:text-orange-600'
                  }`}
                >
                  全部
                </button>
                {Object.entries(CONTENT_CATEGORY_LABELS).map(([key, label]) => {
                  const isActive = category === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setCategory(key as ContentCategory)}
                      className={`h-8 rounded-full border px-4 text-sm transition-colors ${
                        isActive
                          ? 'border-orange-500 bg-orange-500 text-white shadow-sm'
                          : 'border-orange-100 bg-white text-foreground hover:border-orange-300 hover:text-orange-600'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-3">
              <div className="w-10 shrink-0 pt-1.5 text-sm font-semibold text-foreground">时长:</div>
              <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                {DURATION_FILTER_OPTIONS.map((option) => {
                  const isActive =
                    option.value === DURATION_ALL_VALUE
                      ? durationRanges.length === 0
                      : durationRanges.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleDurationFilterClick(option.value)}
                      className={`h-8 rounded-full border px-4 text-sm transition-colors ${
                        isActive
                          ? 'border-orange-500 bg-orange-500 text-white shadow-sm'
                          : 'border-orange-100 bg-white text-foreground hover:border-orange-300 hover:text-orange-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {hasActiveFilters && (
              <div className="flex items-center justify-between gap-3 rounded-xl border border-orange-100 bg-orange-50/30 px-3 py-2 text-sm">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="font-medium text-foreground">已选条件:</span>
                  {selectedDurationFilters.map((filter) => (
                    <button
                      key={filter.value}
                      type="button"
                      onClick={() =>
                        setDurationRanges((current) =>
                          current.filter((item) => item !== filter.value),
                        )
                      }
                      className="rounded-full border border-orange-100 bg-white px-3 py-1 text-xs text-foreground hover:text-orange-600"
                    >
                      {filter.label} ×
                    </button>
                  ))}
                  {category && selectedCategoryLabel && (
                    <button
                      type="button"
                      onClick={() => setCategory('')}
                      className="rounded-full border border-orange-100 bg-white px-3 py-1 text-xs text-foreground hover:text-orange-600"
                    >
                      {selectedCategoryLabel} ×
                    </button>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                  className="h-7 shrink-0 px-2 text-xs text-orange-700 hover:bg-orange-50 hover:text-orange-800"
                >
                  清空
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="mb-3 rounded-2xl border border-orange-100 bg-white p-3 shadow-sm md:hidden">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">共 {filteredTopics.length} 个主题</p>
              <p className="mt-1 truncate text-sm font-medium text-foreground">
                {selectedFilterSummary}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsMobileFilterOpen(true)}
              className="h-9 shrink-0 rounded-full border-orange-200 bg-white px-3 text-orange-700 hover:bg-orange-50"
            >
              <Filter className="mr-1.5 h-4 w-4" />
              筛选
            </Button>
          </div>
          {hasActiveFilters && (
            <div className="mt-2 flex items-center justify-between gap-2 border-t border-orange-50 pt-2">
              <span className="truncate text-xs text-muted-foreground">
                已选：{selectedFilterSummary}
              </span>
              <button
                type="button"
                onClick={handleClearFilters}
                className="shrink-0 text-xs font-medium text-orange-700"
              >
                清空
              </button>
            </div>
          )}
        </div>

        {isCoarsePointer && (
          <div className="mb-3 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">浏览方式</span>
            <div className="inline-flex rounded-full border border-orange-100 bg-white p-0.5 shadow-sm">
              <button
                type="button"
                onClick={() => setMobileViewMode('card')}
                className={`inline-flex h-8 items-center gap-1 rounded-full px-2.5 text-xs font-medium transition-colors ${
                  mobileViewMode === 'card'
                    ? 'bg-orange-500 text-white'
                    : 'text-muted-foreground hover:text-orange-600'
                }`}
              >
                <Rows3 className="h-3.5 w-3.5" />
                卡片
              </button>
              <button
                type="button"
                onClick={() => setMobileViewMode('compact')}
                className={`inline-flex h-8 items-center gap-1 rounded-full px-2.5 text-xs font-medium transition-colors ${
                  mobileViewMode === 'compact'
                    ? 'bg-orange-500 text-white'
                    : 'text-muted-foreground hover:text-orange-600'
                }`}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                速览
              </button>
            </div>
          </div>
        )}

        {/* 主题列表 */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredTopics.length === 0 ? (
          <div className="py-20 text-center">
            <div className="mb-4 text-6xl">🔍</div>
            <h3 className="text-lg font-medium">暂无匹配的主题</h3>
            <p className="text-muted-foreground">尝试调整搜索关键词或筛选条件</p>
          </div>
        ) : (
          <div
            className={`grid gap-2 sm:gap-4 ${
              mobileViewMode === 'compact' && isCoarsePointer
                ? 'grid-cols-3'
                : 'grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            }`}
            data-ai-section-type="card-list"
          >
            {filteredTopics.map((topic) => (
              <Card
                key={topic.id}
                className="group cursor-pointer overflow-hidden border-0 bg-white shadow-sm transition-all hover:shadow-md"
                role="button"
                tabIndex={0}
                onClick={() => handleViewDetail(topic)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleViewDetail(topic);
                  }
                }}
              >
                {/* 封面图 */}
                <div className="relative aspect-[2/1] w-full overflow-hidden bg-muted sm:aspect-video">
                  {topic.cover_image ? (
                    <Image
                      src={topic.cover_image}
                      alt={topic.topic_name}
                      className="pointer-events-none h-full w-full select-none object-cover transition-transform duration-500 group-hover:scale-105"
                      width={300}
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                      onDragStart={(e) => e.preventDefault()}
                      style={PROTECTED_PREVIEW_STYLE}
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                      无封面
                    </div>
                  )}
                </div>

                <CardContent
                  className={
                    mobileViewMode === 'compact'
                      ? isCoarsePointer
                        ? 'p-1.5'
                        : 'p-4'
                      : 'p-2 sm:p-4'
                  }
                >
                  {mobileViewMode === 'compact' && isCoarsePointer ? (
                    <>
                      <h3 className="line-clamp-1 text-[12px] font-semibold leading-5 text-foreground">
                        {topic.topic_name}
                      </h3>
                      <span className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-medium text-orange-700">
                        <Clock className="h-2.5 w-2.5" />
                        {topic.estimated_duration}
                      </span>
                    </>
                  ) : mobileViewMode === 'compact' ? (
                    <>
                      <h3 className="mb-3 line-clamp-1 text-[17px] font-semibold leading-tight text-foreground">
                        {topic.topic_name}
                      </h3>
                      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                        <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2 py-1 text-orange-700">
                          <Clock className="h-3.5 w-3.5" />
                          <span className="font-semibold">{topic.estimated_duration}</span>
                        </span>
                        <span className="text-[12px] font-medium text-foreground/85">
                          {CONTENT_CATEGORY_LABELS[topic.content_category]}
                        </span>
                        <span className="text-[12px] text-muted-foreground/70">
                          {CONTENT_DIRECTION_LABELS[topic.content_direction]}
                        </span>
                      </div>
                      <div className={`mt-4 grid gap-2 ${PUBLIC_CATALOG_MODE ? 'grid-cols-1' : 'grid-cols-2'}`}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetail(topic);
                          }}
                          className="border-orange-200 bg-white text-orange-700 hover:bg-orange-50"
                        >
                          查看详情
                        </Button>
                        {!PUBLIC_CATALOG_MODE && (
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenRedeem(topic);
                            }}
                            className="gap-1.5 bg-orange-500 hover:bg-orange-600"
                          >
                            <Gift className="h-3.5 w-3.5" />
                            立即领取
                          </Button>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="mb-1.5 line-clamp-1 text-[14px] font-semibold leading-tight text-foreground sm:mb-3 sm:text-[17px]">
                        {topic.topic_name}
                      </h3>

                      <div className="mb-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs sm:mb-2 sm:gap-x-3">
                        <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-1.5 py-0.5 text-[11px] text-orange-700 sm:px-2 sm:py-1 sm:text-xs">
                          <Clock className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                          <span className="font-semibold">{topic.estimated_duration}</span>
                        </span>
                        <span className="text-[11px] font-medium text-foreground/85 sm:text-[12px]">
                          {CONTENT_CATEGORY_LABELS[topic.content_category]}
                        </span>
                        <span className="hidden text-[11px] text-muted-foreground/70 sm:inline sm:text-[12px]">
                          {CONTENT_DIRECTION_LABELS[topic.content_direction]}
                        </span>
                      </div>

                      <div className={`mt-2.5 grid gap-1.5 sm:mt-4 sm:gap-2 ${PUBLIC_CATALOG_MODE ? 'grid-cols-1' : 'grid-cols-2'}`}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetail(topic);
                          }}
                          className="h-7 border-orange-200 bg-white px-1.5 text-[11px] text-orange-700 hover:bg-orange-50 sm:h-9 sm:px-2 sm:text-sm"
                        >
                          查看详情
                        </Button>
                        {!PUBLIC_CATALOG_MODE && (
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenRedeem(topic);
                            }}
                            className="h-7 gap-1 bg-orange-500 px-1.5 text-[11px] hover:bg-orange-600 sm:h-9 sm:gap-1.5 sm:px-2 sm:text-sm"
                          >
                            <Gift className="hidden h-3.5 w-3.5 sm:block" />
                            立即领取
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <TopicFilterDrawer
        open={isMobileFilterOpen}
        onOpenChange={setIsMobileFilterOpen}
        durationRanges={durationRanges}
        category={category}
        resultCount={filteredTopics.length}
        onDurationToggle={handleDurationFilterClick}
        onCategoryChange={setCategory}
        onClear={handleClearFilters}
      />

      {/* 详情弹窗 */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="flex max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] !max-w-[820px] flex-col overflow-hidden rounded-3xl border-orange-100/80 bg-[#fffdf8] p-0 shadow-[0_18px_60px_rgba(120,72,28,0.18)] sm:h-auto">
          {detailTopic && (
            <>
              {/* 顶部信息区 - 结构化展示 */}
              <div className="border-b border-orange-100/70 bg-gradient-to-br from-orange-50/80 via-[#fffdf8] to-amber-50/70 px-5 pb-4 pt-5 sm:px-8 sm:pb-4 sm:pt-6">
                <DialogTitle className="pr-10 text-2xl font-bold leading-tight text-foreground sm:text-[1.7rem]">
                  {detailTopic.topic_name}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  查看主题预览、核心价值观和内容清单
                </DialogDescription>

                {/* 信息网格 - 清晰有序 */}
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm sm:text-base">
                  {/* 时长 */}
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">时长</span>
                    <span className="inline-flex items-center gap-1.5 font-semibold text-orange-700">
                      <Clock className="h-4 w-4" />
                      {detailTopic.estimated_duration}
                    </span>
                  </div>
                  <span className="hidden h-5 w-px bg-orange-200 sm:block" />

                  {/* 分类 */}
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">分类</span>
                    <span className="font-semibold text-foreground">
                      {CONTENT_CATEGORY_LABELS[detailTopic.content_category]}
                    </span>
                  </div>
                  <span className="hidden h-5 w-px bg-orange-200 sm:block" />

                  {/* 方向 */}
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">方向</span>
                    <span className="font-semibold text-foreground">
                      {CONTENT_DIRECTION_LABELS[detailTopic.content_direction]}
                    </span>
                  </div>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto sm:flex-none sm:overflow-visible">
                <div className="space-y-4 px-5 py-4 pb-5 sm:px-8 sm:py-5">
                  {/* 预览资源缩略图网格 */}
                  {((!HIDE_PPT_PREVIEW && detailTopic.ppt_display) || detailTopic.video_display || detailTopic.script_display) && (
                    <div>
                      <div className="mb-2.5 flex items-center gap-2">
                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-50 text-orange-600">
                          <Eye className="h-4 w-4" />
                        </div>
                        <h4 className="text-base font-semibold text-foreground">内容预览</h4>
                      </div>
                      <div
                        className={getPreviewGridClassName(
                          [!HIDE_PPT_PREVIEW && !!detailTopic.ppt_display, !!detailTopic.video_display, !!detailTopic.script_display].filter(Boolean).length
                        )}
                      >
                        {/* PPT预览缩略图 */}
                        {!HIDE_PPT_PREVIEW && detailTopic.ppt_display && (
                          <div
                            className="group cursor-pointer overflow-hidden rounded-xl border border-orange-100/80 bg-white/90 shadow-sm"
                            onClick={() => setPreviewImage(detailTopic.ppt_display)}
                          >
                            <div className="relative h-[150px] overflow-hidden rounded-xl bg-muted sm:h-[170px]">
                              <img
                                src={detailTopic.ppt_display}
                                alt="PPT预览"
                                className="pointer-events-none h-full w-full origin-top select-none object-cover object-top transition-transform group-hover:scale-105"
                                draggable={false}
                                onContextMenu={(e) => e.preventDefault()}
                                onDragStart={(e) => e.preventDefault()}
                                style={PROTECTED_PREVIEW_STYLE}
                              />
                              <div className="pointer-events-none absolute inset-0 overflow-hidden">
                                <div className="grid h-full w-full grid-cols-3 place-items-center gap-y-3 px-2 py-2">
                                  {VIDEO_WATERMARK_ITEMS.map((item) => (
                                    <span
                                      key={`ppt-watermark-${item}`}
                                      className="select-none whitespace-nowrap text-[10px] font-semibold tracking-[0.14em]"
                                      style={{
                                        transform: 'rotate(-22deg)',
                                        color: 'rgba(51, 65, 85, 0.26)',
                                        textShadow: '0 0 1px rgba(255,255,255,0.55)',
                                      }}
                                    >
                                      仅供预览
                                    </span>
                                  ))}
                                </div>
                              </div>
                              {/* 类型标签 */}
                              <div className="absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                                PPT
                              </div>
                              {/* 悬停遮罩 */}
                              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                                <FileText className="h-6 w-6 text-white" />
                                <span className="mt-1 text-xs text-white">点击查看</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {/* 视频预览缩略图 - 点击前仅显示封面，避免请求视频流量 */}
                        {detailTopic.video_display && (
                          <div
                            className="group cursor-pointer overflow-hidden rounded-xl border border-orange-100/80 bg-white/90 shadow-sm"
                            onClick={() => setPreviewVideo(detailTopic.video_display)}
                          >
                            <div className="relative h-[150px] overflow-hidden rounded-xl bg-muted sm:h-[170px]">
                              <video
                                src={detailTopic.video_display}
                                poster={detailTopic.cover_image || undefined}
                                className="h-full w-full object-cover"
                                preload="none"
                              />
                              {/* 播放按钮居中 */}
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black/50 transition-transform group-hover:scale-110">
                                  <Play className="h-5 w-5 text-white fill-white" />
                                </div>
                              </div>
                              {/* 类型标签 */}
                              <div className="absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                                视频
                              </div>
                            </div>
                          </div>
                        )}
                        {/* 文稿预览缩略图 */}
                        {detailTopic.script_display && (
                          <div
                            className="group cursor-pointer overflow-hidden rounded-xl border border-orange-100/80 bg-white/90 shadow-sm"
                            onClick={() => setPreviewImage(detailTopic.script_display!)}
                          >
                            <div className="relative h-[150px] overflow-hidden rounded-xl bg-muted sm:h-[170px]">
                              <img
                                src={detailTopic.script_display}
                                alt="文稿预览"
                                className="pointer-events-none h-full w-full select-none object-cover transition-transform group-hover:scale-105"
                                draggable={false}
                                onContextMenu={(e) => e.preventDefault()}
                                onDragStart={(e) => e.preventDefault()}
                                style={PROTECTED_PREVIEW_STYLE}
                              />
                              <div className="pointer-events-none absolute inset-0 overflow-hidden">
                                <div className="grid h-full w-full grid-cols-3 place-items-center gap-y-3 px-2 py-2">
                                  {VIDEO_WATERMARK_ITEMS.map((item) => (
                                    <span
                                      key={`doc-watermark-${item}`}
                                      className="select-none whitespace-nowrap text-[10px] font-semibold tracking-[0.14em]"
                                      style={{
                                        transform: 'rotate(-22deg)',
                                        color: 'rgba(51, 65, 85, 0.26)',
                                        textShadow: '0 0 1px rgba(255,255,255,0.55)',
                                      }}
                                    >
                                      仅供预览
                                    </span>
                                  ))}
                                </div>
                              </div>
                              {/* 类型标签 */}
                              <div className="absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                                文稿
                              </div>
                              {/* 悬停遮罩 */}
                              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                                <FileText className="h-6 w-6 text-white" />
                                <span className="mt-1 text-xs text-white">点击查看</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">点击缩略图查看详情</p>
                    </div>
                  )}

                  {/* 核心价值观 */}
                  {detailTopic.core_value && (
                    <div>
                      <div className="mb-2.5 flex items-center gap-2">
                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-50 text-orange-600">
                          <Sparkles className="h-4 w-4" />
                        </div>
                        <h4 className="text-base font-semibold text-foreground">核心价值观</h4>
                      </div>
                      <div className="rounded-2xl bg-gradient-to-r from-amber-50/90 to-orange-50/70 px-4 py-3">
                        <p className="text-sm leading-6 text-foreground sm:text-base">
                          {detailTopic.core_value}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* 包含资源 - 更有品质感的商品化模块 */}
                  <div>
                    <div className="mb-2.5 flex items-center gap-2">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-50 text-orange-600">
                        <FileText className="h-4 w-4" />
                      </div>
                      <h4 className="text-base font-semibold text-foreground">内容清单</h4>
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-orange-100/80 bg-white/90 shadow-sm">
                      {RESOURCE_ITEMS.map((item) => {
                        const Icon = item.icon;
                        return (
                          <div
                            key={item.title}
                            className="flex items-center gap-3 border-b border-orange-100/70 px-4 py-2.5 last:border-b-0"
                          >
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-orange-50/90 text-orange-600">
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="grid min-w-0 flex-1 gap-0.5 sm:grid-cols-[128px_1fr] sm:items-center sm:gap-3">
                              <p className="font-semibold text-foreground">{item.title}</p>
                              {item.description && (
                                <p className="text-xs leading-5 text-muted-foreground sm:text-sm">{item.description}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* 底部操作区 */}
              <div className="shrink-0 border-t border-orange-100/80 bg-white/90 px-5 py-3 sm:px-8">
                {!PUBLIC_CATALOG_MODE && (
                  <p className="mb-3 text-sm text-muted-foreground">
                    {contactConfig.enabled ? contactConfig.detail_description : '支持兑换码领取、单独获取、续费咨询'}
                  </p>
                )}
                <div className={`grid gap-3 ${contactConfig.enabled && !PUBLIC_CATALOG_MODE ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                  {contactConfig.enabled && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleContactAction('detail')}
                      className="h-11 border-orange-200 text-orange-700 hover:bg-orange-50"
                    >
                      {contactConfig.button_text}
                    </Button>
                  )}
                  {!PUBLIC_CATALOG_MODE && (
                    <Button
                      type="button"
                      className="h-11 gap-2 bg-orange-500 font-semibold text-white hover:bg-orange-600"
                      onClick={() => {
                        setIsDetailOpen(false);
                        handleOpenRedeem(detailTopic);
                      }}
                    >
                      <Gift className="h-4 w-4" />
                      立即领取
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* 兑换码验证弹窗 */}
      {!PUBLIC_CATALOG_MODE && <Dialog open={isPasscodeDialogOpen} onOpenChange={(open) => {
        setIsPasscodeDialogOpen(open);
        if (!open) setPasscodeError('');
      }}>
        <DialogContent className="max-w-md max-sm:top-[1rem] max-sm:max-h-[calc(100dvh-2rem)] max-sm:translate-y-0 max-sm:overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{passcodeDialogMode === 'login' ? '会员登录' : '输入兑换码'}</DialogTitle>
            <DialogDescription>
              {passcodeDialogMode === 'login'
                ? '请输入会员兑换码登录后查看会员权益与领取记录'
                : '请输入您的会员兑换码以领取主题内容'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="text"
              placeholder="请输入兑换码"
              value={passcode}
              onChange={(e) => {
                setPasscode(e.target.value);
                if (passcodeError) setPasscodeError('');
              }}
              aria-invalid={Boolean(passcodeError)}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              onKeyDown={(e) => e.key === 'Enter' && handleVerifyPasscode()}
              className="h-12 text-center text-lg tracking-widest"
              disabled={isVerifying}
            />

            <p className="mt-1 text-xs text-muted-foreground">
              兑换码为 6–32 位数字或英文字母；最外侧空白会自动去除，内部空格、中文和特殊符号不支持。
            </p>

            {passcodeError && (
              <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-sm leading-5 text-red-700" role="alert">
                {passcodeError}
              </p>
            )}

            {contactConfig.enabled && (
              <div className="mt-3 flex items-center gap-2 rounded-md border border-dashed bg-orange-50/40 px-3 py-2">
                <p className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
                  {contactConfig.passcode_hint}
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 shrink-0 border-orange-200 bg-white px-2.5 text-xs text-orange-700 hover:bg-orange-100"
                  onClick={() => handleContactAction('passcode')}
                >
                  {contactConfig.button_text}
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsPasscodeDialogOpen(false)}
              disabled={isVerifying}
            >
              取消
            </Button>
            <Button
              onClick={handleVerifyPasscode}
              disabled={!passcode.trim() || isVerifying}
            >
              {isVerifying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  验证中...
                </>
              ) : (
                passcodeDialogMode === 'login' ? '登录' : '验证并领取'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>}

      {/* 领取确认弹窗 */}
      {!PUBLIC_CATALOG_MODE && <Dialog open={!!confirmTopic} onOpenChange={(open) => !open && setConfirmTopic(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>确认领取</DialogTitle>
            <DialogDescription>
              您即将领取「{confirmTopic?.topic_name}」，请确认会员权益信息
            </DialogDescription>
          </DialogHeader>
          {session && (
            <div className="py-4">
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  会员类型: <span className="font-medium text-foreground">{session.member.member_type}</span>
                </p>
                <p>
                  {cycleRemainLabel}: <span className="font-medium text-foreground">{session.remainingCycle}</span>
                </p>
                {!isTotalMode && (
                  <p>
                    今日剩余次数: <span className="font-medium text-foreground">{session.remainingToday}</span>
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmTopic(null)}
              disabled={isRedeeming}
            >
              取消
            </Button>
            <Button onClick={confirmRedeem} disabled={isRedeeming}>
              {isRedeeming ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  领取中...
                </>
              ) : (
                '确认领取'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>}

      {/* 图片预览弹窗 */}
      <Dialog open={!!previewImage} onOpenChange={(open) => !open && setPreviewImage(null)}>
        <DialogContent className="max-h-[calc(100dvh-1rem)] w-fit !max-w-[calc(100vw-1rem)] overflow-y-auto border-0 bg-neutral-950 p-0 shadow-2xl sm:max-h-none sm:w-full sm:!max-w-4xl sm:overflow-visible sm:border sm:bg-white [&>button]:z-20 [&>button]:rounded-full [&>button]:bg-black/70 [&>button]:p-2 [&>button]:text-white [&>button]:opacity-100 [&>button:hover]:bg-black/90 sm:[&>button]:bg-transparent sm:[&>button]:text-neutral-900 sm:[&>button:hover]:bg-neutral-100">
          <DialogTitle className="sr-only">PPT 预览大图</DialogTitle>
          <DialogDescription className="sr-only">
            查看当前主题的完整 PPT 预览长图
          </DialogDescription>
          <div
            className="relative w-fit max-w-full bg-neutral-950 sm:flex sm:w-full sm:items-center sm:justify-center sm:bg-black/5"
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
            style={PROTECTED_PREVIEW_STYLE}
          >
            {previewImage && (
              <>
                <img
                  src={previewImage}
                  alt="预览大图"
                  className="pointer-events-none block h-auto w-auto max-w-[calc(100vw-1rem)] select-none sm:max-h-[80vh] sm:max-w-full sm:object-contain"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  style={PROTECTED_PREVIEW_STYLE}
                />
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                  <div className="grid h-full w-full grid-cols-3 place-items-center gap-y-8 px-4 py-6 sm:gap-y-12 sm:px-8">
                    {VIDEO_WATERMARK_ITEMS.map((item) => (
                      <span
                        key={`image-preview-watermark-${item}`}
                        className="select-none whitespace-nowrap text-sm font-semibold tracking-[0.3em] sm:text-lg"
                        style={{
                          transform: 'rotate(-24deg)',
                          color: 'rgba(51, 65, 85, 0.24)',
                          textShadow: '0 0 2px rgba(255,255,255,0.55)',
                        }}
                      >
                        仅供预览
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 客服弹窗 */}
      {!PUBLIC_CATALOG_MODE && <Dialog open={isContactDialogOpen} onOpenChange={setIsContactDialogOpen}>
        <DialogContent className="max-w-xs px-5 py-5">
          <DialogHeader className="items-center pb-1 text-center">
            <DialogTitle className="text-base">{contactConfig.title || '联系客服'}</DialogTitle>
            <DialogDescription className="text-xs">{contactConfig.description || '支持购买、续费、单独获取主题与找回兑换码'}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-1">
            {contactConfig.wechat_id?.trim() && (
              <div className="rounded-xl border bg-muted/20 px-3 py-2.5">
                <div className="flex min-h-9 items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">微信号</p>
                    <p className="truncate font-mono text-sm text-foreground">{contactConfig.wechat_id.trim()}</p>
                  </div>
                  <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={handleCopyWechatId}>
                    <Copy className="mr-1 h-3 w-3" />
                    复制
                  </Button>
                </div>
              </div>
            )}

            {contactConfig.qr_image_url?.trim() ? (
              <div className="flex flex-col items-center justify-center rounded-xl border bg-white px-3 py-3">
                <img
                  src={contactConfig.qr_image_url.trim()}
                  alt="客服二维码"
                  className="mx-auto h-36 w-36 rounded object-contain"
                />
                <p className="mt-2 text-center text-[10px] text-muted-foreground">可截图扫码添加微信</p>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed bg-muted/20 p-3 text-center text-xs text-muted-foreground">
                暂未配置二维码图片
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>}

      {/* 视频预览弹窗 */}
      <Dialog open={!!previewVideo} onOpenChange={(open) => !open && setPreviewVideo(null)}>
        <DialogContent className="max-w-4xl p-0">
          <DialogTitle className="sr-only">视频预览</DialogTitle>
          <DialogDescription className="sr-only">
            播放当前主题的演讲示范视频
          </DialogDescription>
          <div className="relative flex items-center justify-center bg-black">
            {previewVideo && (
              <>
                <video
                  src={previewVideo}
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  controlsList="nodownload noplaybackrate"
                  disablePictureInPicture
                  onContextMenu={(e) => e.preventDefault()}
                  className="max-h-[80vh] w-full"
                >
                  您的浏览器不支持视频播放
                </video>
                <div className="pointer-events-none absolute right-3 top-3 z-20 rounded bg-white/75 px-2 py-1 text-xs font-semibold text-slate-900/85 shadow-sm sm:right-4 sm:top-4 sm:text-sm">
                  此资源包含消音有字幕、无字幕视频
                </div>
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                  <div className="grid h-full w-full grid-cols-3 place-items-center gap-y-8 px-4 py-6 sm:gap-y-12 sm:px-8">
                    {VIDEO_WATERMARK_ITEMS.map((item) => (
                      <span
                        key={item}
                        className="select-none whitespace-nowrap text-sm font-semibold tracking-[0.3em] text-white/20 sm:text-lg"
                        style={{ transform: 'rotate(-24deg)' }}
                      >
                        仅供预览
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicTopicListPage;


